import { z } from 'zod';

/**
 * Which backend the screen is pointed at.
 *
 * ## A scope, not a filter
 *
 * The flags screens have an environment axis too, and copying it here would be
 * wrong. A flag is one row in one shared table partitioned by environment, so
 * its four values belong to the same object and `EnvironmentMatrix` compares
 * them side by side. A client is not: it lives in `veev-{env}-tenants-catalog`
 * with its own Cognito pools and its own identity-api, so the four are four
 * separate stores and a client in dev does not exist in prd at all. Nothing to
 * compare — so this decides which backend the whole screen talks to.
 *
 * ## `eph` needs a slot, and cannot be represented without one
 *
 * Upstream inserts the slot as its own segment of every resource name when the
 * environment is `eph`, so two slots share nothing but a prefix. Without one
 * there is no deployment to address, which is why {@link formatTarget} refuses
 * to produce a string for it rather than emitting something that would key a
 * cache and address nothing.
 */

export const CLIENT_ENVIRONMENTS = ['dev', 'stg', 'prd', 'eph'] as const;
export const ClientEnvironment = z.enum(CLIENT_ENVIRONMENTS);
export type ClientEnvironment = z.infer<typeof ClientEnvironment>;

export const ENVIRONMENT_LABEL: Record<ClientEnvironment, string> = {
    dev: 'Dev',
    stg: 'Staging',
    prd: 'Production',
    eph: 'Ephemeral slot',
};

export interface ClientTarget {
    readonly env: ClientEnvironment;
    /** Present only for `eph`, where it is required. */
    readonly slot?: ClientSlot;
}

/**
 * Every ephemeral slot the platform runs.
 *
 * A closed set, not a convention: the ten stages are provisioned by IaC and a
 * pull request takes one rather than inventing one. That is what lets the
 * control be a `<select>` — and once it is, there is nothing left to normalise.
 * `DEV-1` is not a slot spelled oddly, it is not a slot; membership decides,
 * and anything outside the set is simply no slot at all.
 *
 * The API deliberately does NOT know this list. Upstream derives a slot's SSM
 * scope from the slot itself, which is what makes a brand-new stage need zero
 * configuration; closing the set there would turn adding one into a Backoffice
 * deployment. This is an affordance for the picker, not a contract.
 */
export const CLIENT_SLOTS = [
    'dev1',
    'dev2',
    'dev3',
    'dev4',
    'dev5',
    'qa1',
    'qa2',
    'qa3',
    'qa4',
    'qa5',
] as const;

export type ClientSlot = (typeof CLIENT_SLOTS)[number];

export function isClientSlot(value: string): value is ClientSlot {
    return (CLIENT_SLOTS as readonly string[]).includes(value);
}

/** `null` when an ephemeral target names no slot — there is nothing to address. */
export function formatTarget(target: ClientTarget): string | null {
    if (target.env !== 'eph') return target.env;

    return target.slot ? `eph:${target.slot}` : null;
}

/** What the operator sees: `Production`, or `Ephemeral slot · dev1`. */
export function describeTarget(target: ClientTarget): string {
    if (target.env !== 'eph') return ENVIRONMENT_LABEL[target.env];

    return target.slot ? `Ephemeral slot · ${target.slot}` : 'Ephemeral slot · none';
}

/** The query string every request carries. */
/**
 * What a deployment can address, in picker order.
 *
 * ## Why `dev` is the only one with a choice
 *
 * Ephemeral review slots are stages inside the dev account, so the dev
 * deployment reaches them with the credentials it already has — its own
 * execution role, in the account they run in. Nothing analogous exists for
 * `stg` or `prd`: each reaches one account, its own, so their deployments have
 * exactly one addressable target and the picker disappears entirely.
 */
const REACHABLE: Record<ClientEnvironment, readonly ClientEnvironment[]> = {
    dev: ['dev', 'eph'],
    stg: ['stg'],
    prd: ['prd'],
    // A deployment is never `eph` itself: review slots are backend stages, and
    // the Backoffice does not ship into one. Listed only so the map is total —
    // if that ever changes, it reaches exactly itself.
    eph: ['eph'],
};

export function reachableEnvironments(deployEnv: ClientEnvironment): readonly ClientEnvironment[] {
    return REACHABLE[deployEnv];
}

export function isTargetReachable(target: ClientTarget, deployEnv: ClientEnvironment): boolean {
    return reachableEnvironments(deployEnv).includes(target.env);
}

export function targetParams(target: ClientTarget): Record<string, string> {
    return target.env === 'eph' && target.slot
        ? { env: target.env, slot: target.slot }
        : { env: target.env };
}

/**
 * The target as the URL spells it, read against what this deployment can reach.
 *
 * Pure, and outside the hook, because everything interesting here is about a
 * URL the app did not produce: a bookmark from another deployment, a link
 * pasted into Slack, a query string edited by hand. Each is answered the same
 * way — by falling back to something addressable rather than by trusting the
 * string. An unreachable environment becomes the deployed one, and an unknown
 * slot becomes no slot, which the screens already know how to ask about.
 */
export function readTarget(params: URLSearchParams, deployEnv: ClientEnvironment): ClientTarget {
    const parsed = ClientEnvironment.safeParse(params.get('env'));
    const requested = parsed.success ? parsed.data : deployEnv;
    const env = reachableEnvironments(deployEnv).includes(requested) ? requested : deployEnv;

    const slot = params.get('slot') ?? '';
    return env === 'eph' && isClientSlot(slot) ? { env, slot } : { env };
}
